/**
 * Presentation-only block that renders a single correction's body — target,
 * score delta, candidate, timestamp, rationale.
 *
 * Used by the audit table (audit-section), the parked queue panel, and the
 * corpus drawer's per-dimension rationale view. Each surface wraps its own
 * expand/collapse, status badges, and action buttons (revert/delete) around
 * this body — those are NOT this component's concern.
 *
 * The class names and `data-testid` attributes are kept identical to the
 * original audit-section JSX so existing audit tests keep working.
 */

export interface CorrectionRowData {
  id: string;
  /** e.g. "Evidências", "Skills · leveling", "Pesos do eixo" */
  targetSection: string;
  /** e.g. "Estabilidade", "PM", "GCA" */
  targetDetail: string;
  originalScore: string;
  correctedScore: string;
  rationale: string;
  candidateName?: string | null;
  /** ISO string; the component formats it via toLocaleString. */
  correctedAt: string;
}

export function CorrectionRow({
  row,
  rationaleId,
}: {
  row: CorrectionRowData;
  /** When provided, the rationale node gets this id so a parent button can
   *  reference it via aria-describedby — keeps the row's accessible name a
   *  short summary instead of swallowing the operator's free-text rationale. */
  rationaleId?: string;
}): JSX.Element {
  const when = new Date(row.correctedAt);
  return (
    <>
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-sm font-medium" data-testid={`audit-target-${row.id}`}>
          <span className="text-muted-foreground">{row.targetSection} · </span>
          {row.targetDetail}
        </span>
        <span className="text-sm text-muted-foreground">
          <span className="tabular-nums" data-testid={`audit-original-${row.id}`}>
            {row.originalScore}
          </span>
          <span className="mx-1">→</span>
          <span className="tabular-nums font-medium" data-testid={`audit-corrected-${row.id}`}>
            {row.correctedScore}
          </span>
        </span>
        {row.candidateName && (
          <span className="text-xs text-foreground/80" data-testid={`audit-candidate-${row.id}`}>
            sobre <span className="font-medium">{row.candidateName}</span>
          </span>
        )}
        <span className="ml-auto text-xs text-muted-foreground tabular-nums">
          {when.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
        </span>
      </div>
      <div
        id={rationaleId}
        className="mt-1 text-xs text-muted-foreground truncate"
        data-testid={`audit-rationale-${row.id}`}
      >
        {row.rationale || <span className="italic">(sem motivo registrado)</span>}
      </div>
    </>
  );
}
